import type { ScheduleAiTheme } from 'src/theme/schedule-ai-overrides';

import { useTranslation } from 'react-i18next';

import { Box, Button, Tooltip, MenuList, MenuItem, useTheme } from '@mui/material';

import { useUserPermissions } from 'src/hooks/use-user-permissions';
import { useAutomationCheck } from 'src/hooks/use-automation-check';

import { capitalize } from 'src/utils/change-case';

import { PermissionKeys } from 'src/auth/types';
import { useAuthContext } from 'src/auth/hooks';

import { StatusMap, ActivityStatus, ActivityStatusTransitionMap } from 'src/types/dto';
import { isManualActivityCancellationBlocked } from 'src/utils/schedule-modification-lock';

import { Iconify } from '../iconify';
import { usePopover, CustomPopover } from '../custom-popover';

export const StatusDropdown = ({
  status,
  onChange,
  disabled,
  /** When set, ACTIVITIES_UNDER_MY_PROJECTS on update permissions applies only if user matches this project owner id */
  activityProjectOwnerId,
  /** When true, a booking-request workflow is in flight; the control is locked with an explanatory tooltip. */
  bookingRequestInProgress = false,
}: {
  status: ActivityStatus;
  onChange: (status: ActivityStatus) => void;
  disabled?: boolean;
  activityProjectOwnerId?: number | string | null;
  bookingRequestInProgress?: boolean;
}) => {
  const popover = usePopover();
  const theme: ScheduleAiTheme = useTheme();
  const visitColorScheme = theme.visit;
  const { t } = useTranslation('activities');
  const { user } = useAuthContext();
  const { hasAnyPermission } = useUserPermissions();
  const { hasPreRequestAutomation, hasRequestAutomation } = useAutomationCheck();
  // Get status colors from scheduleAiOverrides
  const getStatusColors = (status: ActivityStatus) => {
    if (visitColorScheme[status as keyof typeof visitColorScheme]) {
      return visitColorScheme[status as keyof typeof visitColorScheme];
    }
    // Fallback for CREATED and other missing statuses
    return {
      PRIMARY: 'text.primary',
      SECONDARY: 'grey.100',
      BORDER: 'grey.300',
      BORDER_WIDTH: 2,
      BORDER_STYLE: 'solid',
    };
  };

  const getDropdownOptions = (status: ActivityStatus) => {
    const options = ActivityStatusTransitionMap[status] || [];
    return options.filter((option) => {
      if (option === ActivityStatus.PRE_REQUESTED && !hasPreRequestAutomation()) return false;
      if (option === ActivityStatus.REQUESTED && !hasRequestAutomation()) return false;
      if (
        option === ActivityStatus.CANCELLED &&
        isManualActivityCancellationBlocked({
          status,
          bookingRequestInProgress,
        })
      ) {
        return false;
      }
      return true;
    });
  };

  const statusColors = getStatusColors(status);
  const statusLabel = StatusMap[status] || capitalize(status);
  const transitionOptions = getDropdownOptions(status);
  const hasOptions = transitionOptions.length > 0;
  const isActivityProjectOwner =
    activityProjectOwnerId != null &&
    user?.id != null &&
    Number(activityProjectOwnerId) === Number(user.id);
  const canUpdateStatusAny = hasAnyPermission([
    { permission: PermissionKeys.UPDATE_SERVICE, levels: ['GROUP', 'ALL'] },
    { permission: PermissionKeys.UPDATE_SERVICE_STATUS, levels: ['GROUP', 'ALL'] },
  ]);
  const canUpdateStatusOnOwnedProject =
    isActivityProjectOwner &&
    hasAnyPermission([
      { permission: PermissionKeys.UPDATE_SERVICE, levels: ['ACTIVITIES_UNDER_MY_PROJECTS'] },
      { permission: PermissionKeys.UPDATE_SERVICE_STATUS, levels: ['ACTIVITIES_UNDER_MY_PROJECTS'] },
    ]);
  const canUpdateStatus = canUpdateStatusAny || canUpdateStatusOnOwnedProject;
  const sharedStyles = {
    borderWidth: statusColors.BORDER_WIDTH || 1,
    borderColor: statusColors.BORDER,
    color: statusColors.PRIMARY,
    fontWeight: 'fontWeightMedium',
    fontSize: '0.75rem',
    backgroundColor: statusColors.SECONDARY,
    borderStyle: statusColors.BORDER_STYLE || 'solid',
  } as const;

  if (!hasOptions) {
    return (
      <Box
        sx={{
          ...sharedStyles,
          display: 'inline-flex',
          alignItems: 'center',
          borderRadius: 0.75,
          px: 1,
          py: 0.25,
          lineHeight: 1.5,
        }}
      >
        {statusLabel}
      </Box>
    );
  }

  return (
    <>
      <Tooltip
        title={
          bookingRequestInProgress
            ? t('activityModal.actions.bookingRequestInProgress')
            : ''
        }
        arrow
      >
        {/* span keeps the tooltip working while the button is disabled */}
        <span>
          <Button
            size="small"
            variant="outlined"
            onClick={popover.onOpen}
            endIcon={<Iconify icon="ic:round-keyboard-arrow-down" width={18} height={18} />}
            sx={{
              ...sharedStyles,
              px: 1,
              py: 0.25,
              minHeight: 0,
              lineHeight: 1.5,
              borderRadius: 0.75,
              "&:hover": {
                borderColor: statusColors.BORDER,
                color: statusColors.PRIMARY,
                backgroundColor: statusColors.SECONDARY,
              },
            }}
            disabled={!canUpdateStatus || disabled || bookingRequestInProgress}
          >
            {statusLabel}
          </Button>
        </span>
      </Tooltip>

      <CustomPopover open={popover.open} anchorEl={popover.anchorEl} onClose={popover.onClose}>
        <MenuList>
          {transitionOptions?.map((option) => (
            <MenuItem key={option} onClick={() => {
              popover.onClose();
              onChange(option);
            }}>
              {StatusMap[option]}
            </MenuItem>
          ))}
        </MenuList>
      </CustomPopover>
    </>
  );
};
